import { useState } from 'react';
import {
  Box,
  Grid,
  Link,
  Panel,
  PanelActions,
  PanelHeader,
  PanelInset,
  PanelTitle,
  Stack,
  Text,
  TextField,
} from '@customerio/pluma-components/react';
import { ObjectAttributeList } from '@/features/objects';
import { filteredAttrsWithIdentifiers, isObjectAttributeIdentifier } from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';
import { useObjectDetail } from './context';
import { ObjectRecentActivity } from './object-recent-activity';
import { ObjectRelationshipsPreview } from './object-relationships-preview';

const ENABLE_SEARCH_AFTER = 10;

type AttributePartition = {
  identifiers: Array<Record<string, string>>;
  attributes: Array<Record<string, string>>;
};

function partitionAttributes(allAttrs: Array<Record<string, string>>, searchTerm: string): AttributePartition {
  const partition: AttributePartition = { identifiers: [], attributes: [] };
  // Ember matches case-sensitively; lowered here so "Plan" still finds "plan".
  const loweredTerm = searchTerm.toLowerCase();
  for (const attr of allAttrs) {
    const [key = ''] = Object.keys(attr);
    if (!isEmptyString(loweredTerm) && !key.toLowerCase().includes(loweredTerm)) continue;
    (isObjectAttributeIdentifier(key) ? partition.identifiers : partition.attributes).push(attr);
  }
  return partition;
}

type AttributeSectionProps = {
  title: string;
  attributes: Array<Record<string, string>>;
  /** Skip the Identifier badge (the section header labels them already). */
  ignoreIdentifiers?: boolean;
  actions?: React.ReactNode;
};

function AttributeSection({ title, attributes, ignoreIdentifiers, actions }: AttributeSectionProps) {
  const detail = useObjectDetail();
  if (attributes.length === 0) return null;
  return (
    <>
      <PanelHeader withBorder py="100" style={{ borderTop: 'none' }}>
        <Text weight="bold">{title}</Text>
        {actions}
      </PanelHeader>
      <PanelInset pb="200">
        <ObjectAttributeList
          attributes={attributes}
          ignoreIdentifiers={ignoreIdentifiers}
          isMultiIdentifier={detail.isMultiIdentifier}
        />
      </PanelInset>
    </>
  );
}

function AttributesPanel() {
  const detail = useObjectDetail();
  const { record, hrefs } = detail;
  const [searchTerm, setSearchTerm] = useState('');

  const allAttrs = filteredAttrsWithIdentifiers(record);
  const isSearchEnabled = allAttrs.length >= ENABLE_SEARCH_AFTER;
  const { identifiers, attributes } = partitionAttributes(allAttrs, searchTerm.trim());
  const hasMatches = identifiers.length + attributes.length > 0;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle size="md">Attributes</PanelTitle>
        <PanelActions>
          <Link
            href={hrefs.tabs.attributes}
            isUnderlined={false}
            icon="chevron-right"
            iconPosition="trailing"
            data-test-attributes-link="more"
          >
            View all
          </Link>
        </PanelActions>
      </PanelHeader>

      {isSearchEnabled ? (
        <PanelHeader>
          <TextField
            ariaLabel="Attribute name"
            data-test-attributes-search
            placeholder="Find by attribute name"
            value={searchTerm}
            onInput={(event) => {
              if (event.target instanceof HTMLInputElement) setSearchTerm(event.target.value);
            }}
            icon="search"
            w="full"
          />
        </PanelHeader>
      ) : null}

      {hasMatches ? (
        <>
          <AttributeSection
            title="Identifiers"
            attributes={identifiers}
            ignoreIdentifiers
            actions={
              detail.canConfigureObjects ? (
                <PanelActions>
                  <Link
                    href={hrefs.attributesEdit}
                    isUnderlined={false}
                    icon="chevron-right"
                    iconPosition="trailing"
                    data-test-attributes-link="manage"
                  >
                    Manage
                  </Link>
                </PanelActions>
              ) : null
            }
          />
          <AttributeSection title="Attributes" attributes={attributes} />
        </>
      ) : (
        <Box textAlign="center" data-test-attributes-not-found>
          <Text>No attributes for this object matching "{searchTerm}"</Text>
        </Box>
      )}
    </Panel>
  );
}

export function ObjectOverview() {
  return (
    <Grid columns="1fr 2fr" alignItems="flex-start" gap="300">
      <Box>
        <AttributesPanel />
      </Box>
      <Stack gap="300">
        <ObjectRecentActivity />
        <ObjectRelationshipsPreview />
      </Stack>
    </Grid>
  );
}
